import DashedDivider from '@components/Divider/DashedDivider';
import PreviewImage from '@components/InputDocuments/PreviewImage';
import PreviewOrDownloadFileButton from '@components/InputDocuments/PreviewOrDownloadFileButton';
import { FlexRow as Row } from '@components/FlexRow';
import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { checkPropertiesIsHaveValueWithIncludes } from '@helper/validation.utils';
import { isEmpty, startCase } from 'lodash';
import { Certificate, CertificateFile, Language, Level } from '../types';
import { formatCertificateDateRange } from '../utils/languageUtils';

interface LanguageItemProps {
    itemLanguage: Language | null;
    itemLevel: Level | null;
    itemCertificates: Certificate[];
    onPressEdit: () => void;
    onPressDelete: () => void;
    isDivide?: boolean;
}

const INCOMPLETE_FIELDS_WITH_CERTIFICATE = ['number', 'url', 'endDate', 'file'];
const INCOMPLETE_FIELDS_WITHOUT_CERTIFICATE = [
    'number', 'url', 'endDate', 'file', 'issuer', 'obtainedResult', 'startDate', 'title', '_id',
];

const isLanguageEntryIncomplete = (
    itemLanguage: Language | null,
    itemLevel: Level | null,
    itemCertificates: Certificate[],
): boolean => {
    const hasCertificate = !isEmpty(itemCertificates?.[0]);
    const excludedFields = hasCertificate
        ? INCOMPLETE_FIELDS_WITH_CERTIFICATE
        : INCOMPLETE_FIELDS_WITHOUT_CERTIFICATE;

    return !checkPropertiesIsHaveValueWithIncludes(
        { ...itemCertificates?.[0], ...itemLanguage, ...itemLevel },
        excludedFields,
    );
};

const LanguageItem = ({
    itemLanguage,
    itemLevel,
    itemCertificates,
    onPressEdit,
    onPressDelete,
    isDivide = false,
}: LanguageItemProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [previewImage, setPreviewImage] = useState<CertificateFile | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);
    const isIncomplete = isLanguageEntryIncomplete(itemLanguage, itemLevel, itemCertificates);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    return (
        <React.Fragment>
            <PreviewImage
                title={previewImage?.name}
                src={previewImage?.url}
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
            />
            <Box
                sx={{
                    borderRadius: '1rem',
                    backgroundColor: isIncomplete
                        ? theme.palette.errorRed?.main ? `${theme.palette.errorRed.main}20` : '#FCEEEE'
                        : 'transparent',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: theme.spacing(2),
                    position: 'relative',
                }}>
                <Stack sx={{ flex: 1 }}>
                    <Row justifyContent={'space-between'} alignItems={'flex-start'}>
                        <Stack sx={{ flex: 1 }}>
                            <Typography
                                sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 700,
                                    fontSize: isMobile ? 14 : 18,
                                }}>
                                {startCase(itemLanguage?.name ?? '-')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 0.5 : 1 }}>
                                <Typography component="span" sx={{ color: theme.palette.text.secondary, fontSize: isMobile ? 12 : 16 }}>
                                    Niveau :
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        ml: '5px',
                                        color: theme.palette.text.primary,
                                        fontWeight: 500,
                                        fontSize: isMobile ? 12 : 16,
                                    }}>
                                    {itemLevel?.level ?? '-'}
                                </Typography>
                            </Box>

                            {!isEmpty(itemCertificates) && !isMobile && (
                                <Typography sx={{ mb: 0.5, color: theme.palette.primary.main, fontWeight: 500, fontSize: 16 }}>
                                    {formatCertificateDateRange(
                                        itemCertificates[0].startDate,
                                        itemCertificates[0].endDate,
                                    )}
                                </Typography>
                            )}

                            {!isEmpty(itemCertificates) && (
                                <Row alignItems={'start'} gap={1}>
                                    <svg width="16" height="24" viewBox="0 0 16 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g clipPath="url(#clip0_1_7168)">
                                            <path
                                                d="M1.00003 20.0056C1.13147 20.0059 1.26165 19.9801 1.38306 19.9298C1.50447 19.8794 1.61469 19.8055 1.70736 19.7123L5.67403 15.743L6.15937 16.245C6.8042 16.9219 7.69141 17.3151 8.62603 17.3383C8.94777 17.3389 9.26725 17.2845 9.5707 17.1776C10.0421 17.0113 10.456 16.7137 10.7639 16.3199C11.0717 15.9261 11.2605 15.4525 11.308 14.955C11.3775 14.2984 11.3294 13.6347 11.166 12.995L11.138 12.8616L13.3967 10.6036L13.698 10.9056C13.9198 11.1386 14.2166 11.2858 14.5362 11.3214C14.8559 11.357 15.1778 11.2787 15.4454 11.1003C15.6021 10.9883 15.7326 10.8436 15.8278 10.6762C15.923 10.5088 15.9806 10.3227 15.9967 10.1308C16.0128 9.93889 15.987 9.74577 15.9211 9.56483C15.8551 9.38389 15.7506 9.21947 15.6147 9.08296L10.97 4.42763C10.7483 4.19467 10.4515 4.04743 10.1318 4.01182C9.81221 3.97621 9.49027 4.05452 9.2227 4.23296C9.06599 4.34491 8.93551 4.48961 8.8403 4.65702C8.7451 4.82443 8.68744 5.01054 8.67133 5.20245C8.65522 5.39436 8.68104 5.58748 8.74701 5.76842C8.81298 5.94936 8.91752 6.11378 9.05337 6.25029L9.40003 6.60029L7.1467 8.85429C6.49269 8.68291 5.81301 8.63164 5.1407 8.70296C4.62181 8.75632 4.12902 8.95705 3.72053 9.28143C3.31204 9.60582 3.00489 10.0403 2.83537 10.5336C2.6525 11.0362 2.61665 11.5805 2.73201 12.1027C2.84736 12.625 3.10915 13.1035 3.4867 13.4823L4.2867 14.3063L0.292698 18.299C0.152983 18.4388 0.05787 18.617 0.0193788 18.8109C-0.0191124 19.0049 0.000745575 19.2059 0.0764433 19.3885C0.152141 19.5711 0.280281 19.7273 0.444671 19.8371C0.60906 19.9469 0.802321 20.0056 1.00003 20.0056ZM4.7207 11.2003C4.76505 11.064 4.84797 10.9434 4.95944 10.8533C5.0709 10.7631 5.20611 10.7072 5.3487 10.6923C5.78562 10.6466 6.22721 10.6804 6.65203 10.7923L7.2047 10.933C7.37278 10.9758 7.54911 10.9741 7.71637 10.9282C7.88363 10.8823 8.03606 10.7936 8.1587 10.671L10.8134 8.01496L11.9827 9.18829L9.34003 11.8316C9.22251 11.9492 9.13615 12.0942 9.08877 12.2536C9.0414 12.4129 9.0345 12.5816 9.0687 12.7443L9.2187 13.453C9.32925 13.8751 9.36267 14.3137 9.31737 14.7476C9.31105 14.8699 9.26786 14.9873 9.19349 15.0846C9.11911 15.1818 9.01703 15.2542 8.9007 15.2923C8.66132 15.3486 8.41093 15.3362 8.17826 15.2566C7.9456 15.177 7.7401 15.0334 7.58537 14.8423L4.91337 12.079C4.8007 11.9662 4.72268 11.8235 4.68854 11.6678C4.65439 11.5121 4.66555 11.3499 4.7207 11.2003Z"
                                                fill={theme.palette.text.disabled}
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_1_7168">
                                                <rect width="16" height="16" fill="white" transform="translate(0 4)" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <Stack sx={{ gap: '3px' }}>
                                        <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
                                            <Typography component="span" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                                                {(itemCertificates[0].title ?? '').toUpperCase()}
                                            </Typography>
                                            <Typography component="span" sx={{ ml: '5px', color: theme.palette.text.secondary }}>
                                                ({(itemCertificates[0].issuer?.name ?? '').toUpperCase()})
                                            </Typography>
                                        </Stack>
                                        <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
                                            <Typography component="span" sx={{ color: theme.palette.text.secondary }}>
                                                Numéro du certificat:
                                            </Typography>
                                            <Typography component="span" sx={{ ml: '5px', color: theme.palette.text.secondary }}>
                                                {itemCertificates[0].number ?? '-'}
                                            </Typography>
                                        </Stack>
                                        <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
                                            <Typography component="span" sx={{ color: theme.palette.text.secondary }}>
                                                Résultat obtenu:
                                            </Typography>
                                            <Typography component="span" sx={{ ml: '5px', color: theme.palette.text.secondary }}>
                                                {itemCertificates[0].obtainedResult ?? '-'}
                                            </Typography>
                                        </Stack>
                                        <Stack sx={{ display: 'flex', flexDirection: 'row' }}>
                                            <Typography component="span" sx={{ color: theme.palette.text.secondary }}>
                                                URL du certificat:
                                            </Typography>
                                            <Typography component="span" sx={{ ml: '5px', color: theme.palette.text.secondary }}>
                                                {itemCertificates[0].url ?? '-'}
                                            </Typography>
                                        </Stack>
                                        {isMobile && !isEmpty(itemCertificates) && (
                                            <Typography
                                                sx={{
                                                    mt: 0.5,
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 500,
                                                    fontSize: 12,
                                                }}>
                                                {formatCertificateDateRange(
                                                    itemCertificates[0].startDate,
                                                    itemCertificates[0].endDate,
                                                )}
                                            </Typography>
                                        )}
                                        <Row gap={2}>
                                            {itemCertificates[0].file?.name && (
                                                <PreviewOrDownloadFileButton
                                                    style={{
                                                        padding: isMobile ? 8 : 12,
                                                        border: `1px solid ${theme.palette.neutralBlack?.main ?? theme.palette.text.primary}`,
                                                        borderRadius: '999px',
                                                        marginTop: 16,
                                                    }}
                                                    textStyle={{
                                                        fontSize: isMobile ? 12 : 14,
                                                        color: theme.palette.neutralBlack?.main ?? theme.palette.text.primary,
                                                        fontWeight: 700,
                                                    }}
                                                    action="preview"
                                                    hideFileSize
                                                    file={itemCertificates[0].file}
                                                />
                                            )}
                                        </Row>
                                    </Stack>
                                </Row>
                            )}
                        </Stack>
                        {/* Action menu */}
                        <Box>
                            <IconButton
                                aria-label="more"
                                aria-controls={menuOpen ? 'language-action-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={menuOpen ? 'true' : undefined}
                                onClick={handleMenuOpen}
                                size="small"
                                sx={{
                                    borderRadius: '4px',
                                    color: theme.palette.text.secondary,
                                    '&:hover': { backgroundColor: theme.palette.action.hover },
                                }}>
                                <MoreVertIcon fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                            <Menu
                                id="language-action-menu"
                                anchorEl={anchorEl}
                                open={menuOpen}
                                onClose={handleMenuClose}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                                <MenuItem onClick={() => { handleMenuClose(); onPressEdit(); }}>
                                    <i className="fi fi-rr-edit" style={{ marginRight: 8, fontSize: isMobile ? 14 : 16 }} />
                                    Modifier
                                </MenuItem>
                                <MenuItem onClick={() => { handleMenuClose(); onPressDelete(); }}>
                                    <i
                                        className="fi fi-rr-trash"
                                        style={{ marginRight: 8, fontSize: isMobile ? 14 : 16, color: theme.palette.errorRed?.main ?? '#d32f2f' }}
                                    />
                                    Supprimer
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Row>
                </Stack>
            </Box>
            {isDivide && (
                <Stack sx={{ pb: 3.25, pt: 4 }}>
                    <DashedDivider />
                </Stack>
            )}
        </React.Fragment>
    );
};

export default LanguageItem;
